<template>
    <tailwind-layout title="Manage Bank Accounts">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center gap-16">
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Bank Accounts</h1>
                    <p class="text-xs">This is where you manage your bank accounts for rent payments. Please verify your identity and complete the bank onboarding process to access the rent payments feature.</p>
                </div>
                <div v-if="accounts.length" class="flex items-center space-x-4">
                    <button @click="createLinkToken('auth')"
                        class="primaryButton">
                        Add Bank Account
                    </button>
                </div>
            </div>
        </template>
        <div class="">
            <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <li v-if="!is_kyc" class="col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
                    <div class="flex w-full items-center justify-between space-x-6 p-6">
                        <div class="w-full">
                            <div class="flex items-center space-x-3">
                                <h3 class="truncate text-lg font-medium text-gray-900">Identity Verification</h3>
                                <span
                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset"
                                    :class="is_kyc ? 'bg-green-50  text-green-700 ring-green-600/20' : 'bg-red-50  text-red-700 ring-red-600/20'">{{
                                    is_kyc ? 'Completed' : 'Pending' }}</span>
                            </div>
                            <p class="mt-4 text-sm text-gray-500">
                                We use Plaid to securely verify your identity. Your information is encrypted and protected.
                            </p>
                        </div>
                    </div>
                    <div v-if="!is_kyc">
                        <div class="-mt-px flex divide-x divide-gray-200">
                            <div class="flex w-0 flex-1 justify-center p-4">
                                <button @click="createLinkToken('identity_verification')" class="primaryButton">
                                    Verify Your Identity
                                </button>
                            </div>
                        </div>
                    </div>
                </li>
                <li v-if="accounts.length === 0" class="col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
                    <div class="flex w-full items-center justify-between space-x-6 p-6">
                        <div class="w-full">
                            <div class="flex items-center space-x-3">
                                <h3 class="truncate text-lg font-medium text-gray-900">Bank Account</h3>
                                <span
                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset"
                                    :class="accounts.length > 0 ? 'bg-green-50  text-green-700 ring-green-600/20' : 'bg-red-50  text-red-700 ring-red-600/20'">{{
                                    accounts.length > 0 ? 'Completed' : 'Pending' }}</span>
                            </div>
                            <p v-if="accounts.length === 0" class="mt-4 text-sm text-gray-500">
                                To receive rent payments, securely link your bank account using Plaid. This ensures fast and reliable transactions. Your information is encrypted and protected throughout the process.
                            </p>
                            <ul v-else class="mt-4 space-y-2">
                                <li class="flex items-center justify-between text-sm">
                                    <span class="font-medium text-gray-700">Bank Name:</span>
                                    <span class="text-gray-600">{{ accounts[0].bank_name }}</span>
                                </li>
                                <li class="flex items-center justify-between text-sm">
                                    <span class="font-medium text-gray-700">Account Title:</span>
                                    <span class="text-gray-600">{{ accounts[0].account_name }}</span>
                                </li>
                                <li class="flex items-center justify-between text-sm">
                                    <span class="font-medium text-gray-700">Account Mask:</span>
                                    <span class="text-gray-600">{{ accounts[0].account_last_4 }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div v-if="is_kyc && accounts.length < 1">
                        <div class="-mt-px flex divide-x divide-gray-200">
                            <div class="flex w-0 flex-1 justify-center p-4">
                                <button @click="createLinkToken('auth')" class="primaryButton">
                                    Add Bank Account
                                </button>
                            </div>
                        </div>
                    </div>
                </li>
                <li v-if="transfer_diligence_status !== 'approved'" class="col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
                    <div class="flex w-full items-center justify-between space-x-6 p-6">
                        <div class="w-full">
                            <div class="flex items-center space-x-3 text-wrap">
                                <h3 class="truncate text-lg font-medium text-gray-900">Onboarding Information</h3>
                                <span
                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset"
                                    :class="transfer_diligence_status === 'approved' ? 'bg-green-50  text-green-700 ring-green-600/20' : 'bg-red-50  text-red-700 ring-red-600/20'">
                                    {{ 
                                        transfer_diligence_status === 'approved' ? 'Completed' : 
                                        transfer_diligence_status === 'pending' ? 'Waiting For Approval' : 
                                        transfer_diligence_status === 'more_information_required' ? 'More Information Required' : 
                                        transfer_diligence_status === 'under_review' ? 'Under Review' : 
                                        'Pending' 
                                    }}
                                </span>
                            </div>
                            <p v-if="!transfer_diligence_status || transfer_diligence_status === 'approved'" class="mt-4 text-sm text-gray-500">
                                Please provide your onboarding information securely through Plaid. This ensures accurate verification and secure processing of payments. Your information is protected throughout the process.
                            </p>
                            <alert-card v-else-if="transfer_diligence_status === 'pending'" :type="'info'">
                                <p>
                                    Your onboarding approval is in progress. Once approved, you will be able to access rent payment features. Please visit this page to check your status.
                                </p>
                            </alert-card>
                            
                            <ul v-else-if="transfer_diligence_status === 'more_information_required'" class="mt-4 space-y-2">
                                <li v-for="(requirement, index) in originator.outstanding_requirements" :key="index" class="flex items-center justify-between text-sm">
                                    <span class="font-medium text-red-700">{{ formatString(requirement.requirement_type) }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div v-if="needs_plaid_owner_verification && accounts.length">
                        <div class="-mt-px flex divide-x divide-gray-200">
                            <div class="flex w-0 flex-1 justify-center p-4">
                                <inertia-link :href="route('plaid.owner-verification')" class="primaryButton">
                                    Complete Owner Verification
                                </inertia-link>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="!is_onboarding_info_submitted && accounts.length">
                        <div class="-mt-px flex divide-x divide-gray-200">
                            <div class="flex w-0 flex-1 justify-center p-4">
                                
                                <button
                                    @click="onboardingStep1"
                                    class="primaryButton">
                                    Submit Onboarding Information
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="!needs_plaid_owner_verification && transfer_diligence_status === 'more_information_required'">
                        <div class="-mt-px flex divide-x divide-gray-200">
                            <div class="flex w-0 flex-1 justify-center p-4">
                                <button
                                    v-if="originator && originator.outstanding_requirements && originator.outstanding_requirements.length"
                                    @click="isShowMissingReqModal = true"
                                    class="primaryButton">
                                    Update Information
                                </button>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <div v-if="accounts.length" class="mt-3">
            <div class="rounded-md lg:shadow shadow-none">
                <div class="flex flex-col">
                    <div class="relative">
                        <div class="align-middle inline-block min-w-full overflow-x-auto">
                            <div class="shadow lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Account Last 4 Digits
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Bank Name
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Account Title
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(account, index) in accounts" :key="account.id"
                                            :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer')]"
                                            >
                                            <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div class="flex">
                                                    {{ account.account_last_4 }}
                                                    <tool-tip
                                                        v-if="index > 0"
                                                            title="Multiple Funding Accounts"
                                                            description="For now we only allow a single bank account to be used for receiving funds for rent payments. COMING SOON - the ability to add more than one bank account and have rent payments funded from multiple properties into different bank accounts. The only bank account today that will receive funds is the bank account labeled Funding Account. If you need assistance to change this bank account please reach out to the support team."
                                                            ></tool-tip>
                                                </div>
                                                <span
                                                    v-if="account.is_funding_account"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50  text-green-700 ring-green-600/20"
                                                    >
                                                    Funding Account
                                                </span>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ account.bank_name }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ account.account_name }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <button v-if="!account.is_funding_account"
                                                    @click="confirmFundingAccountUpdate(account)"
                                                    class="text-blue-600 hover:text-indigo-900 float-right mr-5">
                                                    Set as Funding Account
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-show="!accounts.length">
                                            <td colspan="4"
                                                class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No bank accounts were found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(account, index) in accounts" :key="account.id" class="bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200 col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">
                                                **** **** **** {{ account.account_last_4 }}
                                                <span
                                                    v-if="account.is_funding_account"
                                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50  text-green-700 ring-green-600/20"
                                                    >
                                                    Funding Account
                                                </span>
                                            </h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Bank Name:</span> {{ account.bank_name }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Account Title:</span> {{ account.account_name }}</h4>
                                        </div>
                                        <div>
                                            <div v-if="!account.is_funding_account" class="divide-gray-200 -mt-px flex divide-x">
                                                <div class="flex w-0 flex-1">
                                                    <button
                                                        type="button"
                                                        title="Set as Funding Account"
                                                        @click="confirmFundingAccountUpdate(account)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-star" /> Set as Funding Account
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <modal :open="isShowStepsModal" @closeAlert="isShowStepsModal = false">
            <div class="">
                <h2 class="text-xl font-semibold text-gray-900 text-left">
                    Complete Your Onboarding Steps
                </h2>

                <ol class="space-y-8 text-left mt-8 mb-9">
                    <li v-for="(step, index) in steps" :key="index" class="relative flex-1" :class="{
                        'after:content-[] after:w-0.5 after:h-full after:bg-gray-200 after:inline-block after:absolute after:-bottom-12 after:left-4 lg:after:left-5':
                            index !== steps.length - 1,
                    }">
                        <!-- Completed Step -->
                        <div v-if="step.status === 'completed'" class="flex items-start w-full">
                            <span
                                class="flex-shrink-0 w-8 h-8 bg-green-50 border-2 border-green-600 rounded-full flex justify-center items-center mr-3 text-sm text-green-600 lg:w-10 lg:h-10">
                                <font-awesome-icon :icon="faCheck" class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                            </span>
                            <div class="block">
                                <h4 class="text-lg text-green-600">{{ step.name }}</h4>
                                <span class="text-sm block mt-1">{{ step.description }}</span>
                            </div>
                        </div>


                        <!-- In-Progress Step -->
                        <div v-else-if="step.status === 'in-progress'" class="flex items-start font-medium w-full">
                            <span
                                class="flex-shrink-0 w-8 h-8 bg-indigo-50 border-2 border-indigo-600 rounded-full flex justify-center items-center mr-3 text-sm text-indigo-600 lg:w-10 lg:h-10">
                                {{ index + 1 }}
                            </span>
                            <div class="block">
                                <h4 class="text-lg text-indigo-600">{{ step.name }}</h4>
                                <span class="text-sm mt-1">{{ step.description }}</span>
                                <br>
                                <button v-if="step.key === 'kyc'" @click="createLinkToken('identity_verification')"
                                    class="primaryButton">
                                    Verify Your Identity
                                </button>
                                <button v-else-if="step.key === 'bank-account'" @click="createLinkToken('auth')"
                                    class="primaryButton">
                                    Add Bank Account
                                </button>
                                <button v-else-if="step.key === 'onboarding'"
                                    @click="onboardingStep1"
                                    class="mt-2 block justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-900 bg-gold-500 hover:bg-gold-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    Submit Onboarding Information
                                </button>
                            </div>
                        </div>

                        <!-- Remaining Step -->
                        <div v-else class="flex items-start w-full">
                            <span
                                class="flex-shrink-0 w-8 h-8 bg-gray-50 border-2 border-gray-200 rounded-full flex justify-center items-center mr-3 text-sm lg:w-10 lg:h-10">
                                {{ index + 1 }}
                            </span>
                            <div class="block">
                                <h4 class="text-lg text-gray-900">{{ step.name }}</h4>
                                <span class="text-sm block mt-1">{{ step.description }}</span>
                            </div>
                        </div>

                    </li>
                </ol>
            </div>
        </modal>
        <jetstreem-modal :show="isShowMissingReqModal" :max-width="'md'" @close="isShowMissingReqModal = false">
            <div class="m-5">
                <h2 class="text-xl font-semibold text-gray-900 text-left">
                    Provide Missing Information
                </h2>
                <form @submit.prevent="submitMissingRequirements" aria-label="Login Form">
                    <div v-for="req in form.outstanding_requirements" class="text-left mt-4">
                        <div
                            v-if="!['business_address', 'associated_people', 'business_bank_account', 'business_industry', 'business_product_description'].includes(req.name)">
                            <label :for="req.name" class="block text-sm font-medium text-gray-700">
                                {{ formatString(req.name) }} <span v-if="!['address_2'].includes(req.name)" class="text-red-700">*</span>
                            </label>
                            <div class="mt-1">
                                <input v-if="req.type === 'file'" :id="req.name"
                                    @input="req.value = $event.target.files[0]" :type="req.type"
                                    class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2">
                                
                                <select v-else-if="req.name === 'business_org_type'" :id="req.name" v-model="req.value"
                                    
                                    :disabled="user_business_type === 'individual'"
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100"
                                    :class="user_business_type === 'individual' ? 'bg-gray-100' : ''"
                                    >
                                    <option value="SOLE PROPRIETORSHIP">Sole Proprietorship</option>
                                    <option value="LIMITED LIABILITY COMPANY">Limited Liability Company (LLC)</option>
                                    <option value="PRIVATE COMPANY">Private Company</option>
                                    <option value="PUBLIC COMPANY (UNLISTED)">Public Company (Unlisted)</option>
                                    <option value="PUBLIC COMPANY (LISTED)">Public Company (Listed)</option>
                                    <option value="CHARITABLE ORGANIZATION / NOT-FOR-PROFIT">Charitable Organization / Non-Profit</option>
                                    <option value="PARTNERSHIP">Partnership</option>
                                </select>
                                <select v-else-if="req.name === 'state'" :id="req.name" v-model="req.value"
                                    
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100"
                                    >
                                    <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                </select>
                                <input v-else :id="req.name" v-model="req.value" :type="req.type"
                                    class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2">
                                <small v-if="req.name === 'business_ein' && user_business_type === 'individual'" class="text-gray-500">Enter your SSN if you
                                    don’t have
                                    a Business EIN.</small>
                                <small v-if="req.name === 'business_website' && user_business_type === 'individual'" class="text-gray-500">
                                    Enter https://rentrisk.com if you do not have a website.
                                </small>
                                <small v-if="req.name === 'business_name' && user_business_type === 'individual'" class="text-gray-500">
                                    Enter your full name if you do not have a business name.
                                </small>
                                <p v-text="form.errors[req.name]" class="text-red-500 text-sm"></p>
                            </div>
                        </div>
                    </div>
                    <div class="pt-5">
                        <div class="flex justify-end">
                            <button type="button" @click="isShowMissingReqModal = false"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Close</button>

                            <form-button :form="form" type="submit">
                                Save
                            </form-button>
                        </div>
                    </div>
                </form>
            </div>
        </jetstreem-modal>
        <jetstreem-modal :show="isShowStep1Modal" :max-width="'md'" @close="isShowStep1Modal = false">
            <div class="m-5">
                <h2 class="text-xl font-semibold text-gray-900 text-left">
                    Landlord Onboarding
                </h2>
                <form @submit.prevent="submitMissingRequirements" aria-label="Landlord Onboarding Form">
                    <div class="text-left mt-4">
                        <fieldset>
                            <p class="mt-1 text-sm/6 text-gray-600">What type of business are you?</p>
                            <div class="mt-6 space-y-6 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div class="flex items-center">
                                    <input id="individual" v-model="business_type" value="individual" type="radio" checked
                                        class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden">
                                    <label for="individual"
                                        class="ml-3 block text-sm/6 font-medium text-gray-900">Individual</label>
                                </div>
                                <div class="flex items-center">
                                    <input id="business" v-model="business_type" value="business" type="radio"
                                        class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden">
                                    <label for="business" class="ml-3 block text-sm/6 font-medium text-gray-900">Business</label>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                    <div class="pt-5">
                        <div class="flex justify-end">
                            <button type="button" @click="isShowStep1Modal = false"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Close</button>

                            <InertiaLink :href="route('plaid.user-onboarding')" :data="{'business_type': business_type}" class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-800 disabled:bg-purple-300 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                                Save
                            </InertiaLink>
                        </div>
                    </div>
                </form>
            </div>
        </jetstreem-modal>

        <div v-if="showFundingAccountConfirmationModal">
            <TransitionRoot as="template" :show="showFundingAccountConfirmationModal">
                <Dialog as="div" class="relative z-50" @close="showFundingAccountConfirmationModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faCheck" class="h-6 w-6 text-green-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to set this bank account as your funding account?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showFundingAccountConfirmationModal = false">Cancel</button>
                                        <button type="button" @click="updateFundingAccount()" class="text-center flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <full-page-loader v-if="isLoader"></full-page-loader>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import axios from "axios";
import FullPageLoader from "@/Shared/FullPageLoader.vue";
import Modal from "@/Shared/Modal.vue";
import JetstreemModal from "@/Jetstream/Modal.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faCheckCircle, faCheck, faExclamationCircle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import FormButton from '@/Shared/FormButton.vue'
import AlertCard from '@/Shared/AlertCard.vue'
import {Dialog, DialogPanel, DialogTitle, Switch, SwitchGroup, SwitchLabel, TransitionChild, TransitionRoot} from '@headlessui/vue'
import { router } from '@inertiajs/vue3'
import ToolTip from '@/Shared/ToolTip.vue'

export default {
    components: {
        FullPageLoader,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        Modal,
        JetstreemModal,
        FontAwesomeIcon,
        FormButton,
        AlertCard,
        ToolTip
    },
    props: {
        accounts: Object,
        is_kyc: Boolean,
        transfer_diligence_status: String,
        is_onboarding_info_submitted: Boolean,
        needs_plaid_owner_verification: Boolean,
        originator: Object,
        steps: {
            type: Array,
            required: true,
        },
        steps_status: Array,
        user_business_type: String,
        states: Array
    },
    data() {
        return {
            faCheckCircle,
            faCheck,
            faExclamationCircle,
            faInfoCircle,
            isLoader: false,
            isShowStepsModal: false,
            isShowMissingReqModal: false,
            form: this.$inertia.form({
                outstanding_requirements: [],
            }),
            isShowStep1Modal: false,
            business_type: 'individual',
            showFundingAccountConfirmationModal: false,
            newFundingAccountId: null,
        }
    },

    mounted() {
        if (!this.steps_status.kyc || !this.steps_status.bank_account || !this.steps_status.onboarding) {
            this.isShowStepsModal = true;
        }

        // missing required information
        if (this.originator && this.originator.outstanding_requirements && this.originator.outstanding_requirements.length) {
            this.originator.outstanding_requirements.forEach((req) => {
                let type = 'text';
                // normalize to lowercase for consistent comparisons
                let documentTypesNames = [
                    "ssn_card",
                    "driver_license_or_id",
                    "personal_address_validation",
                    "business_address_validation",
                    "irs_document",
                    "business_name_validation"
                ];
                const reqType = (req.requirement_type || '').toLowerCase();
                if (documentTypesNames.includes(reqType)) {
                    type = 'file';
                }
                var value = null;
                if(reqType === 'business_org_type' && this.user_business_type === 'individual') {
                    value = 'SOLE PROPRIETORSHIP';
                }
                if(reqType === 'business_address') {
                    this.form.outstanding_requirements.push({ name: 'address_1', type: type, value: value });
                    this.form.outstanding_requirements.push({ name: 'address_2', type: type, value: value });
                    this.form.outstanding_requirements.push({ name: 'city', type: type, value: value });
                    this.form.outstanding_requirements.push({ name: 'state', type: type, value: value });
                    this.form.outstanding_requirements.push({ name: 'postal_code', type: type, value: value });
                } else {
                    // include person_id when provided by Plaid for person-specific requirements
                    this.form.outstanding_requirements.push({ name: reqType, type: type, value: value, person_id: req.person_id || null });
                }

            })
        }
    },

    methods: {
        onboardingStep1() {
            this.isShowStep1Modal = true;
        },

        async createLinkToken(product) {
            this.isLoader = true;
            try {
                const response = await axios.post(route('plaid.create-link-token'), {
                    product: product,
                });
                if (response.data.status) {
                    this.initializePlaidLink(response.data.public_token, product);
                }

                // Handle the successful response here
            } catch (error) {
                // Handle any errors here
            }
        },

        async initializePlaidLink(public_token, product) {
            this.isShowStepsModal = false;
            this.isLoader = false;
            var handler = Plaid.create({
                token: public_token,  // Use the provided public token directly
                onLoad: function () {
                    // Optional, called when Link loads
                },
                onSuccess: (public_token, metadata) => {
                    this.isLoader = true;
                    if (product === 'auth') {
                        this.finishAuth(public_token, metadata);
                    } else if (product === 'identity_verification') {
                        this.finishIdv(public_token, metadata);
                    }
                },
                onExit: function (err, metadata) {
                    // The user exited the Link flow.
                    if (err != null) {
                        // The user encountered a Plaid API error prior to exiting.
                    }
                    // metadata contains information about the institution
                    // that the user selected and the most recent API request IDs.
                    // Storing this information can be helpful for support.
                },
                onEvent: function (eventName, metadata) {
                    // Optionally capture Link flow events, streamed through
                    // this callback as your users connect an Item to Plaid.
                    // For example:
                    // eventName = "TRANSITION_VIEW"
                    // metadata  = {
                    //   link_session_id: "123-abc",
                    //   mfa_type:        "questions",
                    //   timestamp:       "2017-09-14T14:42:19.350Z",
                    //   view_name:       "MFA",
                    // }
                }
            });

            handler.open();
        },

        async finishAuth(public_token, metadata) {
            const response = await axios.post(route('plaid.exchange-token-and-retrieve-account'), {
                public_token: public_token,
                metadata: metadata,
            });
            this.isLoader = false;
            location.reload();
        },
        async finishIdv(public_token, metadata) {
            const response = await axios.post(route('plaid.idv.submit'), {
                public_token: public_token,
                metadata: metadata,
            });
            this.isLoader = false;

            // Handle the successful response here
            location.reload();
        },

        formatString(input) {
            return input
                .replace(/_/g, ' ') // Replace underscores with spaces
                .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
        },

        submitMissingRequirements() {
            this.form.post(this.route('plaid.onboarding.missing-requirements.store'), {
                onSuccess: () => {
                    this.isShowMissingReqModal = false;
                }
            })
        },
        

        confirmFundingAccountUpdate(account) {
            this.newFundingAccountId = account.id;
            this.showFundingAccountConfirmationModal = true;
        },

        updateFundingAccount() {
            router.visit(route('user-bank-accounts.update-funding-account'), {
                method: 'post',
                data: {
                    account_id: this.newFundingAccountId,
                },
            })
        }
    },
}
</script>
